import React, { useState, useEffect, useMemo, useCallback, useContext, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { secureGet, securePost } from "../../config/api";
import { formatDateTime, formatAmount } from "../../utils/format";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { secureGetPdf } from "../../config/api";
import DeletePopup from "../../components/DeletePopup";
import GeneratePopup from "../../components/GeneratePopUp";
import ProductRow from "../../components/ProductRow";
import LoadingLogo from "../../components/LoadingLogo";
import SnackbarNotification from "../../components/SnackbarNotification";
import Breadcrumbs from "../../components/Breadcrumbs";
import UserContext from "../../Shared/UserContext";

import downloadIcon from "../../icons/printer.svg";
import checkIcon from "../../icons/check.svg";
import chevronDownIcon from "../../icons/chevron-down.svg";
import chevronUpIcon from "../../icons/chevron-upG.svg";

const ORDER_STATUSES = [
  { key: "CREATED", label: "Créé", color: "#3872B0" },
  { key: "VALIDATED", label: "Validé", color: "#3872B0" },
  { key: "PARTIALLY_DELIVERED", label: "Partiellement livré", color: "#FEA26F" },
  { key: "DELIVERED", label: "Livré", color: "#06D6A0" },
];

const OrderDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const { t } = useTranslation();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { state: { role } } = useContext(UserContext);
  const dropdownRef = useRef(null);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const fetchOrder = useCallback(async () => {
    setLoading(true);
    try {
      const response = await secureGet(`/carts/${id}`, showSnackbar, { signal });
      const fetchedOrder = response.data;
      setOrder(fetchedOrder);
      console.log("fetchedOrder", fetchedOrder);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch order:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [id, signal]);

  useEffect(() => {
    fetchOrder();

    return () => {
      abortController.abort();
    };
  }, [id, abortController, fetchOrder]);

  // Close status dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
    };
    if (showStatusDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showStatusDropdown]);

  const handleUpdateStatus = async (status) => {
    setShowStatusDropdown(false);
    setUpdatingStatus(true);
    try {
      const payload = {
        cartId: order.id,
        status,
      };
      console.log("payload", payload);
      await securePost(`/carts/updateStatus`, payload, showSnackbar);
      showSnackbar(t(`confirmation.orderStatusUpdate`), "success");
      fetchOrder();
    } catch (error) {
      console.error("Error updating the order status:", error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const displayOrderStatus = order?.status ?? ORDER_STATUSES[0].key;
  const currentOrderStatus = ORDER_STATUSES.find((s) => s.key === displayOrderStatus) || ORDER_STATUSES[0];

  const renderStatut = () => (

    <div className="flex items-center ">
      <span
        className="px-[5px] py-1 rounded-[10px] font-semibold text-[12px] bg-[#0AD69F] text-white"
      >
        BC
      </span>
      <div className="w-2 h-[2px] bg-[#043F88]"></div>

      <span
        className={`px-[5px] py-1 rounded-[10px] font-semibold text-[12px] ${order.deliveryNote ? "bg-[#0AD69F] text-white" : "bg-[#EF476F] text-white"} ${role === "ROLE_STOREKEEPER" ? "cursor-pointer" : ""}`}
        onClick={role === "ROLE_STOREKEEPER" && order.deliveryNote ? () => handleStatutClick("BL") : null}

      >
        BL
      </span>
    </div>
  );

  const handleStatutClick = (key) => {
    switch (key) {
      case "BL":
        handleConsultBL();
        break;
      default:
        break;
    }
  };

  const handleConsultBL = () => {
    console.log("order.deliveryNote", order.deliveryNote);
    if (order.deliveryNote?.id) {
      console.log("order.deliveryNote.id", order.deliveryNote.id);
      history.push({
        pathname: `/delivery-notes/details/${order.deliveryNote.id}`,
      });
    }
  };

  if (loading) return <LoadingLogo />;


  return (
    <div>
      <Helmet>
        <title>Drivnest - {t("titles.order_details")}</title>
      </Helmet>
      {/* Header Section */}
      <div className="mb-4">
        <Breadcrumbs />
        <h1 className="text-[40px] font-semibold text-[#000000] ">{t(`titles.order_details`)}</h1>
        {/* State and Actions */}
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-[10px]">
            <p className="text-[#000000] font-semibold text-[24px]">
              {order.cartNumber}
            </p>
            <div className="flex space-x-1">{renderStatut()}</div>
          </div>

          <div className="flex items-center gap-4">
            {/* Status Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                disabled={updatingStatus}
                className="px-4 py-2 bg-white text-[14px] font-bold rounded-[12px] flex items-center gap-2 hover:bg-gray-200 transition shadow-[0px_20px_80px_#00000011] disabled:opacity-50"
                style={{ color: currentOrderStatus.color }}
              >
                {currentOrderStatus.label}
                <img
                  src={showStatusDropdown ? chevronUpIcon : chevronDownIcon}
                  alt="chevron"
                  className="w-4 h-4"
                />
              </button>
              {showStatusDropdown && (
                <div className="absolute top-full mt-1 right-0 w-[220px] px-2 bg-white rounded-[12px] py-2 z-10 shadow-[0px_20px_80px_#00000011]">
                  {ORDER_STATUSES.filter((s) => s.key !== displayOrderStatus).map((status, index, array) => (
                    <React.Fragment key={status.key}>
                      <button
                        type="button"
                        onClick={() => handleUpdateStatus(status.key)}
                        className="w-full px-4 py-2 text-left text-[14px] font-semibold hover:bg-gray-100 transition"
                        style={{ color: status.color }}
                      >
                        {status.label}
                      </button>
                      {index < array.length - 1 && (
                        <div className="h-[1px] bg-[#F7F7F7]" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Client Info */}
        <p className="text-primary text-[20px] font-semibold">
          {order.client?.code} - {order.client?.name}
        </p>
      </div>

      {/* Order Information */}
      <div className="w-full flex justify-evenly bg-white py-4 px-4 rounded-[12px] shadow-[0px_6px_26px_#626B7D12] mb-4">
        {/* DATE DE CRÉATION */}
        <div className="flex-1 text-center">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">{t(`rows.creationDate`)}</p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatDateTime(order.createdAt)}
          </p>
        </div>

        {/* Fiche */}
        <div className="flex-1 text-center">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">NUMÉRO DE PROJET</p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {order.projectNumber}
          </p>
        </div>

        {/* TOTAL HT */}
        <div className="flex-1 text-center">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">TOTAL HT</p>
          <p className="text-[#38485C] text-[20px] font-semibold">{formatAmount(order.priceHT)}</p>
        </div>

        {/* TOTAL TTC */}
        <div className="flex-1 text-center">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">TOTAL TTC</p>
          <p className="text-[#38485C] text-[20px] font-semibold">{formatAmount(order.priceTTC)}</p>
        </div>
      </div>


      {/* Product List */}
      <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-4">
        {t(`fields_types.list`)}
      </h3>
      <div className="space-y-4">
        {order.details.map((product, index) => (
          <ProductRow key={index} product={product} />
        ))}
      </div>


      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />

    </div>
  );
};

export default OrderDetails;

